import { useMutation } from '@apollo/client';
import { Button } from '@avila-tek/ui/src';
import React from 'react';
import {
  CANCEL_ORDER,
  CANCEL_PAYMENT_TDC,
  MARK_ORDER_COMPLETE,
  REFUND_ORDER,
} from '../../../graphql/mutation';
import { useNotify, useUser } from '../../../hooks';
import { Order, OrderStatusEnum, PaymentStatusEnum } from '../../../models';
import { paymentStatus } from '../../../static/paymentStatus';

interface OrderButtonsProps {
  order: Order;
  refreshData: () => Promise<void>;
}

function OrderButtons({ order, refreshData }: OrderButtonsProps) {
  const notify = useNotify();
  const [user] = useUser();
  const [loading, setLoading] = React.useState(false);

  const [markOrderCompleteMutation] = useMutation<{
    markOrderCompleted: Order;
  }>(MARK_ORDER_COMPLETE);

  const [cancelOrderMutation] = useMutation<{
    cancelOrder: Order;
  }>(CANCEL_ORDER);

  const [refundOrderMutation] = useMutation<{
    refundOrder: {
      status: string;
      code: string;
    };
  }>(REFUND_ORDER);

  const [cancelPaymentTDC] = useMutation<{
    cancelPaymentTDC: Order;
  }>(CANCEL_PAYMENT_TDC);

  const handleUpdateOrderStatus = async (
    _paymentStatus: PaymentStatusEnum,
    status: OrderStatusEnum
  ) => {
    try {
      if (loading) return;
      setLoading(true);
      const { data } = await markOrderCompleteMutation({
        variables: {
          data: {
            orderId: order?._id,
            adminId: user?._id,
          },
        },
      });
      if (data?.markOrderCompleted?._id) {
        notify('Orden actualizada exitosamente', 'success');
        refreshData();
      } else {
        notify('Error al actualizar la orden', 'error');
      }
    } catch (error) {
      console.error(error);
      notify(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const processCreditCardCancellation = async () => {
    if (
      order.paymentInfo.paymentMethod &&
      order.paymentInfo.paymentMethod.title === 'Tarjeta de Crédito'
    ) {
      const { data: cancelPayment } = await cancelPaymentTDC({
        variables: {
          data: {
            paymentId: order.payment,
          },
        },
      });

      if (cancelPayment?.cancelPaymentTDC?._id) {
        notify('El beneficiario recibirá el reembolso de su pago', 'success');
        return true;
      }
      notify('Hubo un error al reembolsar el pago', 'error');
      return false;
    }
    return true;
  };

  const handleCancelOrder = async () => {
    try {
      if (loading) return;
      setLoading(true);
      const paymentCancelled = await processCreditCardCancellation();
      if (!paymentCancelled) return;
      const { data } = await cancelOrderMutation({
        variables: {
          data: {
            orderId: order?._id,
            adminId: user?._id,
          },
        },
      });
      if (data?.cancelOrder?._id) {
        notify('Orden cancelada exitosamente', 'success');
        refreshData();
      } else {
        notify('Error al cancelar la orden', 'error');
      }
    } catch (error) {
      console.error(error);
      notify(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefundOrder = async () => {
    try {
      if (loading) return;
      setLoading(true);
      const paymentCancelled = await processCreditCardCancellation();
      if (!paymentCancelled) return;
      const { data } = await refundOrderMutation({
        variables: {
          data: {
            orderId: order?._id,
          },
        },
      });
      if (data?.refundOrder?.code) {
        notify('Orden reembolsada exitosamente', 'success');
        refreshData();
      } else {
        notify('Error al reembolsar la orden', 'error');
      }
    } catch (error) {
      console.error(error);
      notify(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 mt-5">
      {order.paymentStatus === paymentStatus.initial &&
      (order.status === 'initial' || order.status === 'receivable') ? (
        <>
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleUpdateOrderStatus(paymentStatus.approved, order.status);
            }}
          >
            Marcar como pagado
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleCancelOrder();
            }}
            className="bg-white border border-danger-200 text-danger-200"
          >
            Reembolsar cliente
          </Button>
        </>
      ) : null}
      {order.status === 'complete' ? (
        <Button
          onClick={(e) => {
            e.preventDefault();
            handleRefundOrder();
          }}
          className="bg-white border border-danger-200 text-danger-200"
        >
          Reembolsar cliente
        </Button>
      ) : null}
    </div>
  );
}

export default OrderButtons;
